import getOr from 'lodash/fp/getOr';
import jwt_decode from "jwt-decode";

import { FireBase } from '../utils/firebase';
import { Auth } from './auth';
import { getTierFromCode } from '../utils/utils';
import { getCookie, setCookie } from '../utils/browser';
import { GLOBAL_CONSTANTS } from '../global/constants';
import { replaceUrl } from '../utils/url';
import { MINI_APP } from '../utils/miniApp';
import { apiLogout } from '../utils/api';

// local storage and session storage
const storage = localStorage || window.localStorage;

export const LOCAL_STORAGE = {
  REDUCER_PATH: "LOCAL_STORAGE",
  LOCAL_KEY: "data",
  VALUE: "value",
  TYPE: {
    token: "t",
    fullname: "fn",
    userEmail: "ue",
    userId: "uid",
    refreshToken: "rt",
    cookiePopup: "cp",
    walkThrought: "wt",
    flashMessage: "fm",
    localtion: "l",
    language: "dc_hl",
    isAccessed: 'iw',
    currency: "dc_cur",
    billing: "dc_bi",
    shipping: "dc_si",
    shippingList: "dc_si_list",
    currencyList: "dc_cur_list",
    ipCountry: "dc_ip_country",
    salePopup: "dc_spp",
    lastProducts: "dc_lp",
    lastStores: "dc_ls",
    lastCategories: "dc_lc",
    lastAuthType: "dc_auth_type",
    listAuthProvider: "dc_auth_provider",
    receiptSource: "dc_rc"
  },
  USER_TYPE_KEY: "fea",
}

export const USER_TYPE = {
  TYPE_COLLABORATOR: 2,
  TYPE_NORMAL: 0,
};

export const USER_TYPE_LABEL = {
  [USER_TYPE.TYPE_COLLABORATOR]: "collaborator-user",
  [USER_TYPE.TYPE_NORMAL]: "normal-user"
};


// get data from local (local variable or session storage or local storage)
export function getLocalInfo(type) {
  if (storage && storage.length) {
    return storage.getItem(type);
  } else {
    return null;
  }
}

// set local data (local variable and session storage and local storage)
export function setLocalInfo(type, value) {
  if (value !== undefined && value !== null) {
    if (storage) {
      storage.setItem(type, value);
    }
  } else {
    if (storage) {
      storage.removeItem(type);
    }
  }
}

export class LocalStorage {
  constructor(token, fullname, userId, refreshToken, cookiePopup, walkThrought, flashMessage, location) {
    this[LOCAL_STORAGE.TYPE.token] = token;
    this[LOCAL_STORAGE.TYPE.fullname] = fullname;
    this[LOCAL_STORAGE.TYPE.userId] = userId;
    this[LOCAL_STORAGE.TYPE.refreshToken] = refreshToken;
    this[LOCAL_STORAGE.TYPE.cookiePopup] = cookiePopup;
    this[LOCAL_STORAGE.TYPE.walkThrought] = walkThrought;
    this[LOCAL_STORAGE.TYPE.flashMessage] = flashMessage;
    this[LOCAL_STORAGE.TYPE.localtion] = location;
  }

  saveToLocalStorage() {
    setLocalInfo(LOCAL_STORAGE.LOCAL_KEY, JSON.stringify(this));
  }

  static fromLocalStorage(data = null) {
    let source = data;
    let result = new LocalStorage();
    if (!data) {
      source = getLocalInfo(LOCAL_STORAGE.LOCAL_KEY);
    }
    if (source && typeof (source) === "string") {
      try {
        source = JSON.parse(source);
      } catch (error) {
        source = null;
      }
    }
    if (source && typeof (source) === "object") {
      for (let key in source) {
        result[key] = source[key];
      }
    }
    return result;
  }

  static getLocalInfo(type, state = null) {
    const data = state !== null ? localStorageSelector(state) : LocalStorage.fromLocalStorage();
    return data[type];
  }

  static setLocalInfo(type, data) {
    let newData = LocalStorage.fromLocalStorage();
    newData[type] = data;
    if (typeof window.setLocal === "function") {
      window.setLocal({
        [LOCAL_STORAGE.VALUE]: newData
      })
    }
    newData.saveToLocalStorage();
  }

  static getUserToken(state = null) {
    const result = LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.token, state);
    const cookieData = getCookie(GLOBAL_CONSTANTS.COOKIE.TOKEN);
    if (cookieData && cookieData !== result) {
      LocalStorage.setUserToken(cookieData);
      return cookieData;
    }
    !cookieData && result && setCookie(GLOBAL_CONSTANTS.COOKIE.TOKEN, result, result ? 3600 * 24 * 365 * 2 : -1, "/");
    return result;
  }

  static isLoggedIn(state = null) {
    return !!LocalStorage.getUserToken(state);
  }

  static setUserToken(token) {
    setCookie(GLOBAL_CONSTANTS.COOKIE.TOKEN, token, token ? 3600 * 24 * 365 * 2 : -1, "/");
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.token, token);
  }

  static getUserName(state = null) {
    let result = LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.fullname, state);
    const cookieData = getCookie(GLOBAL_CONSTANTS.COOKIE.USER_NAME);
    if (cookieData && cookieData !== result) {
      LocalStorage.setUserName(decodeURIComponent(cookieData));
      return decodeURIComponent(cookieData);
    }
    result = result ? decodeURIComponent(result) : result;
    !cookieData && result && setCookie(GLOBAL_CONSTANTS.COOKIE.USER_NAME, result, result ? 3600 * 24 * 365 * 2 : -1, "/");
    return result;
  }

  static setUserName(user) {
    user = user ? encodeURIComponent(user) : user;
    setCookie(GLOBAL_CONSTANTS.COOKIE.USER_NAME, user, user ? 3600 * 24 * 365 * 2 : -1, "/");
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.fullname, user);
  }

  static getUserId(state = null) {
    const result = LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.userId, state);
    const cookieData = getCookie(GLOBAL_CONSTANTS.COOKIE.USER_ID);
    if (cookieData && cookieData !== result) {
      LocalStorage.setUserId(cookieData);
      return cookieData;
    }
    !cookieData && result && setCookie(GLOBAL_CONSTANTS.COOKIE.USER_ID, result, result ? 3600 * 24 * 365 * 2 : -1, "/");
    return result;
  }

  static getAuthProviders() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.listAuthProvider);
  }

  static setAuthProviders(providers) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.listAuthProvider, providers);
  }

  static getUserEmail() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.userEmail);
  }

  static setUserEmail(email) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.userEmail, email);
  }

  static getReceiptSource() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.receiptSource);
  }

  static setReceiptSource(info) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.receiptSource, info);
  }

  static setUserId(userID) {
    setCookie(GLOBAL_CONSTANTS.COOKIE.USER_ID, userID, userID ? 3600 * 24 * 365 * 2 : -1, "/");
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.userId, userID);
  }

  static getRefreshToken() {
    const result = LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.refreshToken);
    const cookieData = getCookie(GLOBAL_CONSTANTS.COOKIE.REFRESH);
    if (cookieData && cookieData !== result) {
      LocalStorage.setRefreshToken(cookieData);
      return cookieData;
    }
    !cookieData && result && setCookie(GLOBAL_CONSTANTS.COOKIE.REFRESH, result, result ? 3600 * 24 * 365 * 2 : -1, "/");
    return result;
  }

  static setRefreshToken(refresh) {
    setCookie(GLOBAL_CONSTANTS.COOKIE.REFRESH, refresh, refresh ? 3600 * 24 * 365 * 2 : -1, "/");
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.refreshToken, refresh);
  }

  static getFlashMessage() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.flashMessage);
  }

  static setFlashMessage(role) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.flashMessage, role);
  }

  static getWalkThrought() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.walkThrought);
  }

  static setWalkThrought(role) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.walkThrought, role);
  }

  static getCookiePopup() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.cookiePopup);
  }

  static setCookiePopup(role) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.cookiePopup, role);
  }

  static getLocation() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.localtion);
  }

  static setLocation(location) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.localtion, location);
  }

  static getLanguage(setLocal = true) {
    let result = LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.language);
    const cookieData = getCookie(GLOBAL_CONSTANTS.COOKIE.LANGUAGE);
    if (cookieData && cookieData !== result) {
      setLocal && LocalStorage.setLanguage(cookieData);
      return cookieData;
    }
    !cookieData && result && setCookie(GLOBAL_CONSTANTS.COOKIE.LANGUAGE, result, 3600 * 24 * 365 * 2, "/");
    return result;
  }

  static setLanguage(code) {
    if (LocalStorage.isMiniApp() && LocalStorage.getLanguage(false) !== code) {
      MINI_APP.language = code;
      MINI_APP.setLanguage(code);
    }
    setCookie(GLOBAL_CONSTANTS.COOKIE.LANGUAGE, code, 3600 * 24 * 365 * 2, "/");
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.language, code);
  }

  static getCurrency(setLocal = true) {
    let result = LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.currency);
    const cookieData = getCookie(GLOBAL_CONSTANTS.COOKIE.CURRENCY);
    if (cookieData && cookieData !== result) {
      setLocal && LocalStorage.setCurrency(cookieData);
      return cookieData;
    }
    !cookieData && result && setCookie(GLOBAL_CONSTANTS.COOKIE.CURRENCY, result, 3600 * 24 * 365 * 2, "/");
    return result;
  }

  static setCurrency(currency) {
    if (LocalStorage.isMiniApp() && LocalStorage.getCurrency(false) !== currency) {
      MINI_APP.currency = currency;
      MINI_APP.setCurrency(currency);
    }
    setCookie(GLOBAL_CONSTANTS.COOKIE.CURRENCY, currency, 3600 * 24 * 365 * 2, "/");
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.currency, currency);
  }

  static getBilling(setLocal = true) {
    let result = LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.billing);
    const cookieData = getCookie(GLOBAL_CONSTANTS.COOKIE.BILLING_COUNTRY);
    if (cookieData && cookieData !== result) {
      setLocal && LocalStorage.setBilling(cookieData);
      return cookieData;
    }
    !cookieData && result && setCookie(GLOBAL_CONSTANTS.COOKIE.BILLING_COUNTRY, result, 3600 * 24 * 365 * 2, "/");
    return result;
  }

  static setBilling(billing) {
    if (LocalStorage.isMiniApp() && LocalStorage.getBilling(false) !== billing) {
      MINI_APP.billingCountry = billing;
      MINI_APP.setBillingCountry(billing);
    }
    setCookie(GLOBAL_CONSTANTS.COOKIE.BILLING_COUNTRY, billing, 3600 * 24 * 365 * 2, "/");
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.billing, billing);
  }

  static getShipping(setLocal = true) {
    let result = LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.shipping);
    const cookieData = getCookie(GLOBAL_CONSTANTS.COOKIE.SHIPPING_COUNTRY);
    if (cookieData && cookieData !== result) {
      setLocal && LocalStorage.setShipping(cookieData);
      return cookieData;
    }
    !cookieData && result && setCookie(GLOBAL_CONSTANTS.COOKIE.SHIPPING_COUNTRY, result, 3600 * 24 * 365 * 2, "/");
    return result;
  }

  static setShipping(shipping) {
    if (LocalStorage.isMiniApp() && LocalStorage.getShipping(false) !== shipping) {
      MINI_APP.shippingCountry = shipping;
      MINI_APP.setShippingCountry(shipping);
    }
    setCookie(GLOBAL_CONSTANTS.COOKIE.SHIPPING_COUNTRY, shipping, 3600 * 24 * 365 * 2, "/");
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.shipping, shipping);
  }

  static getCurrencyList() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.currencyList);
  }

  static setCurrencyList(currencyList) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.currencyList, currencyList);
  }

  static getIpCountry() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.ipCountry);
  }

  static setIpCountry(country) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.ipCountry, country);
  }

  static getShippingList() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.shippingList);
  }

  static setShippingList(shippingList) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.shippingList, shippingList);
  }

  static getIsAccessed() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.isAccessed);
  }

  static setIsAccessed(isAccessed) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.isAccessed, isAccessed);
  }

  static getSalePopup() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.salePopup);
  }

  static setSalePopup(path) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.salePopup, path);
  }

  static getLastProducts() {
    return getLastItems(LOCAL_STORAGE.TYPE.lastProducts, LocalStorage.setLastProducts);
  }

  static setLastProducts(data = []) {
    setLastItems(data, LOCAL_STORAGE.TYPE.lastProducts);
  }

  static addLastProduct(id) {
    addLastItem(id, LocalStorage.getLastProducts, LocalStorage.setLastProducts);
  }

  static removeLastProduct(id) {
    removeLastItem(id, LocalStorage.getLastProducts, LocalStorage.setLastProducts);
  }

  static getLastStores() {
    return getLastItems(LOCAL_STORAGE.TYPE.lastStores, LocalStorage.setLastStores);
  }

  static setLastStores(data = []) {
    setLastItems(data, LOCAL_STORAGE.TYPE.lastStores);
  }

  static addLastStore(id) {
    addLastItem(id, LocalStorage.getLastStores, LocalStorage.setLastStores);
  }

  static removeLastStore(id) {
    removeLastItem(id, LocalStorage.getLastStores, LocalStorage.setLastStores);
  }

  static getLastCategories() {
    return getLastItems(LOCAL_STORAGE.TYPE.lastCategories, LocalStorage.setLastCategories);
  }

  static setLastCategories(data = []) {
    setLastItems(data, LOCAL_STORAGE.TYPE.lastCategories);
  }

  static addLastCategory(id) {
    addLastItem(id, LocalStorage.getLastCategories, LocalStorage.setLastCategories);
  }

  static removeLastCategory(id) {
    removeLastItem(id, LocalStorage.getLastCategories, LocalStorage.setLastCategories);
  }


  static getLastAuthType() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.lastAuthType);
  }

  static setLastAuthType(type) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.lastAuthType, type);
  }

  static decodeToken = (token) => {
    if (token) {
      return jwt_decode(token);
    }
    const cToken = LocalStorage.getUserToken();
    if (cToken) {
      return jwt_decode(cToken);
    } else {
      return {};
    }
  }

  // handle login
  static requireLogin(cb, state = null) {
    if (!LocalStorage.isLoggedIn(state)) {
      FireBase.signOut(function () {
        replaceUrl(Auth.callBackLogin(cb))
      }, function () {
        replaceUrl(Auth.callBackLogin(cb));
      });
      return false;
    }
    $(".main-page").removeClass("require-login");
    return true;
  }

  // check collaborator
  static requireCollaborator(cb) {
    if (!LocalStorage.isCollaborator()) {
      replaceUrl(cb || "/tro-thanh-doi-tac");
      return false;
    }
    $(".main-page").removeClass("require-collaborator");
    return true;
  }

  // set user data token, user info, user id, role
  static setLoginInfo(token, userName, userId, refreshToken, email) {
    LocalStorage.setUserToken(token);
    LocalStorage.setUserName(userName);
    LocalStorage.setUserId(userId);
    LocalStorage.setRefreshToken(refreshToken);
    LocalStorage.setUserEmail(email);
  }

  static logout(cb = null, isCallLogout = true) {
    if (!isCallLogout) {
      LocalStorage.setLoginInfo();
      if (cb) window.location.href = cb;
      return;
    }

    apiLogout(
      () => {
        LocalStorage.setLoginInfo();
        if (cb) window.location.href = cb;
      },
      () => {
        LocalStorage.setLoginInfo();
        if (cb) window.location.href = cb;
      }
    );
  }

  static getTierFromToken(token) {
    const data = LocalStorage.decodeToken(token);
    return data[LOCAL_STORAGE.USER_TYPE_KEY];
  }

  static isCollaborator(token) {
    return this.getTierFromToken(token) === USER_TYPE.TYPE_COLLABORATOR;
  }

  static getPackageExpired(token) {
    const data = LocalStorage.decodeToken(token);
    return getTierFromCode(data.pex) * 1000;
  }

  static hasTrial(token) {
    const data = LocalStorage.decodeToken(token);
    return data.tri;
  }

  static getReferralCode(token) {
    const data = LocalStorage.decodeToken(token);
    return data.cod;
  }

  static getSub(token) {
    const data = LocalStorage.decodeToken(token);
    return data.sub;
  }

  static getIat(token) {
    const data = LocalStorage.decodeToken(token);
    return data.iat;
  }

  static getTokenExpired(token) {
    const data = LocalStorage.decodeToken(token);
    return data.exp * 1000;
  }

  static isMiniApp() {
    return !!getCookie(GLOBAL_CONSTANTS.COOKIE.PLATFORM) || typeof window.DCMessage?.postMessage === "function";
  }

  static isOldApp() {
    return typeof window.DCMessage?.postMessage === "function" && !getCookie(GLOBAL_CONSTANTS.COOKIE.PLATFORM);
  }

  static hasExtension() {
    let installed = $("body").attr("dce");
    return (installed !== undefined && installed !== false) || LocalStorage.isMiniApp();
  }
}

export const localStorageSelector = getOr(new LocalStorage(), `${LOCAL_STORAGE.REDUCER_PATH}.${LOCAL_STORAGE.VALUE}`);

function addLastItem(id, getFunc, setFunc) {
  if (!id) {
    return;
  }
  let list = getFunc();
  if (list.length && extractId(list[list.length - 1]) === id + "") {
    return;
  }
  list = [...list.filter(e => extractId(e) !== id + ""), gentIdWithTimestamp(id)].slice(-10);
  setFunc(list);
}

function removeLastItem(id, getFunc, setFunc) {
  if (!id) {
    return;
  }
  let list = getFunc();
  setFunc(list.filter(e => extractId(e) !== id + ""));
}

function getLastItems(key, setFunc) {
  let result = LocalStorage.getLocalInfo(key);
  const cookieData = getCookie(key);
  if (cookieData && cookieData !== result) {
    result = cookieData ? cookieData.split(",") : [];
    setFunc(result);
    return result;
  }
  !cookieData && result && setCookie(key, result, result ? 3600 * 24 * 365 * 2 : -1, "/");
  return result ? result.split(",") : [];
}

function setLastItems(data = [], key) {
  const value = data.join(",");
  setCookie(key, value, value ? 3600 * 24 * 365 * 2 : -1, "/");
  LocalStorage.setLocalInfo(key, value);
}

function extractId(data) {
  return (data + "").split("-")[0] ?? "";
}

function gentIdWithTimestamp(id) {
  return `${id}-${Math.floor(Date.now() / 1000)}`;
}